﻿import { useEffect, useMemo, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import { useLocation } from 'react-router-dom'
import { API_BASE, apiFetch, storedToken, clearToken, decodeJwt } from './lib/api'
import { requestFcmToken, onForegroundMessage } from './lib/firebase'
import { useLocationPing } from './lib/useLocationPing'
import Sidebar from './components/Sidebar'
import MobileNav from './components/MobileNav'
import AuthModal from './components/AuthModal'
import ProfileCompletionModal from './components/ProfileCompletionModal'
import FeedbackModal from './components/FeedbackModal'
import PhoneOtpModal from './components/PhoneOtpModal'
import Home from './pages/home'
import Reels from './pages/Reels'
import Discover from './pages/Discover'
import GroupDining from './pages/group'
import Invites from './pages/Invites'
import Chats from './pages/Chats'
import Schedule from './pages/Schedule'
import NearbySpots from './pages/NearbySpots'
import Settings from './pages/Settings'
import ProfilePage from './pages/profilePage'
import Restaurant from './pages/restaurant'
import NotificationPage from './pages/notification'
import StreakPage from './pages/streak'
import Spinner from './pages/Spinner'
import ReferAndEarn from './pages/affiliate'

// One key per page load so the IP-based and GPS-based visit pings update the same record
const VISIT_KEY = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`

export default function App() {
  const location = useLocation()
  const [currentUserId, setCurrentUserId]   = useState(null)
  const [currentUser, setCurrentUser]       = useState(null)
  const [users, setUsers]                   = useState([])
  const [dietFilter, setDietFilter]         = useState('')
  const [cityFilter, setCityFilter]         = useState('')
  const [availableCities, setAvailableCities] = useState([])
  const [onlyAvailable, setOnlyAvailable]   = useState(false)
  const [mealTime, setMealTime]             = useState(new Date(Date.now() + 3600 * 1000).toISOString().slice(0, 16))
  const [loading, setLoading]               = useState(false)
  const [loadingMore, setLoadingMore]       = useState(false)
  const [hasMoreUsers, setHasMoreUsers]     = useState(false)
  const [usersPage, setUsersPage]           = useState(0)
  const [gpsCity, setGpsCity]               = useState('')
  const [inviteStatus, setInviteStatus]     = useState('')
  const [showAuthModal, setShowAuthModal]   = useState(false)
  const [showCompleteProfile, setShowCompleteProfile] = useState(false)
  const [activeTab, setActiveTab]           = useState('discover')
  const [requests, setRequests]             = useState([])
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [messages, setMessages]             = useState({})
  const [ratings, setRatings]               = useState([])
  const [ratingText, setRatingText]         = useState('')
  const [ratingScore, setRatingScore]       = useState(0)
  const [sidebarOpen, setSidebarOpen]       = useState(false)
  const [showFeedback, setShowFeedback]     = useState(false)
  const [profileUserId, setProfileUserId]   = useState(null)
  // Phone OTP modal state — pendingInviteReceiverId holds the target while user verifies phone
  const [showPhoneOtp, setShowPhoneOtp]     = useState(false)
  const [pendingInviteReceiverId, setPendingInviteReceiverId] = useState(null)
  const [groupChats, setGroupChats]         = useState([])
  const socketRef                           = useRef(null)
  const loadingUsersRef                     = useRef(false)
  const gpsLocationRef                      = useRef(null) // { latitude, longitude, city, address }
  const [typingUsers, setTypingUsers]       = useState({})
  const [unreadChats, setUnreadChats]       = useState({}) // { [requestId]: count }
  const [toast, setToast]                   = useState(null) // { title, body }
  const [sharedGroupEventId, setSharedGroupEventId] = useState(null)
  const [sharedReelId, setSharedReelId] = useState(null)
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  const [streakCount, setStreakCount] = useState(0)
  const [composerRequest, setComposerRequest] = useState(null)
  const [needsWebPushRegistration, setNeedsWebPushRegistration] = useState(false)
  const pushRegistrationInFlightRef = useRef(false)

  /* ── Live location: refreshed and stored every minute while signed in ── */
  const { coords: myCoords, error: locationError } = useLocationPing(Boolean(currentUserId))

  function buildTrackingContext() {
    const params = new URLSearchParams(window.location.search)
    // Persist referral code for use during signup
    const ref = params.get('ref')?.trim().toUpperCase() || null
    if (ref) sessionStorage.setItem('coeater_ref', ref)
    const gclid = params.get('gclid')
    const fbclid = params.get('fbclid')
    const gadCampaignId = params.get('gad_campaignid')
    // Infer platform from click IDs when utm_source is absent
    let sourcePlatform = params.get('utm_source') || null
    if (!sourcePlatform) {
      if (gclid || params.get('gad_source')) sourcePlatform = 'google'
      else if (fbclid) sourcePlatform = 'facebook'
    }
    return {
      referrer: document.referrer || null,
      landingPage: window.location.href || null,
      source: params.get('source') || null,
      sourcePlatform,
      sourceChannel: params.get('utm_medium') || null,
      campaignName: params.get('utm_campaign') || null,
      campaignId: params.get('utm_id') || params.get('campaign_id') || gadCampaignId || gclid || fbclid || params.get('msclkid') || null,
      campaignTerm: params.get('utm_term') || null,
      campaignContent: params.get('utm_content') || null,
      referralCode: ref,
      referralMedium: ref ? (params.get('src') === 'qr' ? 'qr' : 'link') : null,
      visitKey: VISIT_KEY,
    }
  }

  useEffect(() => {
    const match = location.pathname.match(/^\/group\/(\d+)\/?$/i)
    if (!match) return
    setActiveTab('group')
    setSharedGroupEventId(Number(match[1]))
  }, [location.pathname])

  /* ── Load available cities for filter dropdown ── */
  useEffect(() => {
    apiFetch('/users/cities').then(async r => {
      if (r.ok) setAvailableCities(await r.json())
    }).catch(() => {})
  }, [])

  /* ── GPS city detection (works for logged-out users too) ── */
  useEffect(() => {
  let isMounted = true;

  const isMetaWebView = /FBAN|FBAV|Instagram/i.test(navigator.userAgent);

  const searchParams = new URLSearchParams(window.location.search);
  const trackingMetaData = {
    utmSource: searchParams.get('utm_source'),
    utmMedium: searchParams.get('utm_medium'),
    utmCampaign: searchParams.get('utm_campaign'),
    fbclid: searchParams.get('fbclid'),
    isMetaWebView
  };

  // Step 1: Immediate Visit Tracking with IP-based Location Fallback
  const recordVisitWithIPFallback = async () => {
    let initialCity = null;
    let initialLatitude = null;
    let initialLongitude = null;

    // Fast IP lookup fallback (useful for Meta WebViews & desktop browsers)
    try {
      const ipRes = await fetch('https://ipapi.co/json/');
      if (ipRes.ok) {
        const ipData = await ipRes.json();
        if (ipData?.city) {
          initialCity = ipData.city;
          initialLatitude = ipData.latitude || null;
          initialLongitude = ipData.longitude || null;
          if (isMounted) setGpsCity(initialCity);
        }
      }
    } catch {
      /* non-critical fallback */
    }

    const payload = {
      latitude: initialLatitude,
      longitude: initialLongitude,
      city: initialCity,
      address: null,
      ...buildTrackingContext(),
    };

    gpsLocationRef.current = payload;

    // Guaranteed visit record
    apiFetch('/track/visit', {
      method: 'POST',
      body: JSON.stringify(payload)
    }).catch(() => {});
  };

  recordVisitWithIPFallback();

  // Step 2: Request precise GPS coordinates (Increased timeout to 15s)
  if ('geolocation' in navigator && !isMetaWebView) {
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        if (!isMounted) return;

        let detectedCity = null;
        let formattedAddress = null;

        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (apiKey) {
          try {
            const r = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&result_type=locality&key=${apiKey}`
            );
            const data = await r.json();
            if (data.status === 'OK') {
              const comp = data.results?.[0]?.address_components;
              detectedCity =
                comp?.find(c => c.types.includes('locality'))?.long_name ??
                comp?.find(c => c.types.includes('administrative_area_level_2'))?.long_name;
              formattedAddress = data.results?.[0]?.formatted_address || null;
            }
          } catch { /* ignore geocoding errors */ }
        }

        if (detectedCity && isMounted) setGpsCity(detectedCity);

        const locationPayload = {
          latitude,
          longitude,
          city: detectedCity || gpsLocationRef.current?.city || null,
          address: formattedAddress,
          meta: trackingMetaData,
          ...buildTrackingContext(),
        };

        gpsLocationRef.current = locationPayload;

        // Enrich visit record with precise coordinates
        apiFetch('/track/visit', {
          method: 'POST',
          body: JSON.stringify(locationPayload)
        }).catch(() => {});
      },
      (err) => {
        // Silently log without blocking visit tracking
        console.warn('[GPS] Location lookup skipped or timed out:', err.message);
      },
      {
        enableHighAccuracy: false, // Set to false for faster response on desktop/wifi
        timeout: 15000,             // 15 seconds allows user time to click 'Allow'
        maximumAge: 300000          // Use cached position if available within 5 mins
      }
    );
  }

  return () => { isMounted = false; };
}, []);

  /* ── Socket ── */
  function connectSocket() {
    if (socketRef.current?.connected) return
    const token = storedToken()
    if (!token) return
    const s = io(API_BASE, { auth: { token }, transports: ['websocket', 'polling'] })
    s.on('connect', () => {
      setRequests(prev => {
        prev.filter(r => r.status === 'ACCEPTED').forEach(r => s.emit('join_room', r.id))
        return prev
      })
    })
    s.on('new_message', (message) => {
      setMessages(prev => ({
        ...prev,
        [message.requestId]: [...(prev[message.requestId] || []), message]
      }))
      // Increment unread count if the chat is not currently open
      setSelectedRequestId(prevSel => {
        if (prevSel !== message.requestId) {
          setUnreadChats(u => ({ ...u, [message.requestId]: (u[message.requestId] || 0) + 1 }))
        }
        return prevSel
      })
    })
    s.on('message_reaction_updated', ({ messageId, reactions }) => {
      setMessages(prev => {
        const updated = { ...prev }
        for (const [reqId, msgs] of Object.entries(updated)) {
          const idx = msgs.findIndex(m => m.id === messageId)
          if (idx !== -1) {
            const newMsgs = [...msgs]
            newMsgs[idx] = { ...newMsgs[idx], reactions }
            updated[reqId] = newMsgs
          }
        }
        return updated
      })
    })
    s.on('user_typing', ({ requestId }) =>
      setTypingUsers(prev => ({ ...prev, [requestId]: true }))
    )
    s.on('user_stop_typing', ({ requestId }) =>
      setTypingUsers(prev => ({ ...prev, [requestId]: false }))
    )
    s.on('connect_error', err => console.warn('[socket]', err.message))
    s.on('new_group_message', (msg) => {
      // Update groupChats latest message preview when a new group/DM message arrives
      setGroupChats(prev => prev.map(chat => {
        const isGroup = chat.type === 'group' && chat.eventId === msg.groupEventId && !msg.recipientUserId
        const isDm = chat.type === 'dm' && chat.eventId === msg.groupEventId &&
          (chat.otherUserId === msg.senderId || chat.otherUserId === msg.recipientUserId)
        if (!isGroup && !isDm) return chat
        return {
          ...chat,
          latestMessage: {
            id: msg.id,
            text: msg.text,
            senderName: msg.sender?.name || 'Member',
            senderId: msg.senderId,
            createdAt: msg.createdAt,
          }
        }
      }))
    })
    socketRef.current = s
  }

  function disconnectSocket() {
    socketRef.current?.disconnect()
    socketRef.current = null
    setTypingUsers({})
  }

  async function registerWebFcmTokenNow() {
    
    if (pushRegistrationInFlightRef.current) return
    pushRegistrationInFlightRef.current = true
    try {
      const token = await requestFcmToken()
      if (!token) return
      const saveRes = await apiFetch('/users/me/fcm-token', {
        method: 'POST',
        body: JSON.stringify({ token, platform: 'web' })
      })
      if (!saveRes.ok) return
      localStorage.setItem('fcm_token', token)
      setNeedsWebPushRegistration(false)
    } catch { /* non-critical */ }
    finally {
      pushRegistrationInFlightRef.current = false
    }
  }

  // Register web FCM token only when backend is missing it.
  async function registerFcmToken() {
    try {
     
      const statusRes = await apiFetch('/users/me/push-token-status')
      if (!statusRes.ok) return
      const status = await statusRes.json()
      if (status?.web) {
       
        setNeedsWebPushRegistration(false)
        return
      }

      // Browsers can block permission prompts that aren't tied to user interaction.
      // if (Notification.permission === 'default' && !navigator.userActivation?.isActive) {
      //   console.log('[notifications] Web FCM token not registered, but user has not interacted yet. Will register on next interaction. 22')
      //   setNeedsWebPushRegistration(true)
      //   return
      // }
      
      await registerWebFcmTokenNow()
    } catch (err) { 
      console.log('[notifications] Failed to check push token status — will try again later.', err)
      /* non-critical */ }
  }

  useEffect(() => {
    if (!needsWebPushRegistration) return

    const requestOnInteraction = () => {
      setNeedsWebPushRegistration(false)
      registerWebFcmTokenNow()
    }

    window.addEventListener('click', requestOnInteraction, { once: true })
    window.addEventListener('keydown', requestOnInteraction, { once: true })
    window.addEventListener('touchstart', requestOnInteraction, { once: true })

    return () => {
      window.removeEventListener('click', requestOnInteraction)
      window.removeEventListener('keydown', requestOnInteraction)
      window.removeEventListener('touchstart', requestOnInteraction)
    }
  }, [needsWebPushRegistration])

  // Load unread counts from server
  async function loadUnreadCounts() {
    try {
      const res = await apiFetch('/users/me/unread-counts')
      if (res.ok) setUnreadChats(await res.json())
    } catch { /* non-critical */ }
  }

  async function loadGroupChats() {
    try {
      const res = await apiFetch('/group/my-chats')
      if (res.ok) {
        const data = await res.json()
        setGroupChats(Array.isArray(data.chats) ? data.chats : [])
      }
    } catch { /* non-critical */ }
  }

  async function loadUnreadNotifications() {
    if (!currentUserId) {
      setUnreadNotifications(0)
      return
    }
    try {
      const res = await apiFetch('/notification/unread-count')
      if (res.ok) {
        const data = await res.json()
        setUnreadNotifications(Number(data.unreadCount || 0))
      }
    } catch { /* non-critical */ }
  }

  async function loadStreakSummary() {
    if (!currentUserId) {
      setStreakCount(0)
      return
    }
    try {
      const res = await apiFetch('/streak/me')
      if (res.ok) {
        const data = await res.json()
        setStreakCount(Number(data?.summary?.currentStreak || 0))
      }
    } catch { /* non-critical */ }
  }

  /* ── Auto-login from stored token ── */
  useEffect(() => {
    const token = storedToken()
    if (!token) return
    const payload = decodeJwt(token)
    if (!payload || payload.exp * 1000 < Date.now()) { clearToken(); return }
    apiFetch('/auth/me').then(async r => {
      if (r.ok) {
        const user = await r.json()
        setCurrentUserId(user.id)
        setCurrentUser(user)
        if (!user.profileComplete) setShowCompleteProfile(true)
        connectSocket()
        loadUnreadCounts()
        loadUnreadNotifications()
        loadStreakSummary()
        loadGroupChats()
      } else { clearToken() }
    }).catch(() => {})
  }, [])

  // Foreground push notifications → show toast AND a native OS notification
  useEffect(() => {
    const unsub = onForegroundMessage(payload => {
      const { title, body } = payload.notification || {}
      const t = title || 'Co-Eater'
      const b = body || ''
      // In-app toast
      setToast({ title: t, body: b })
      setTimeout(() => setToast(null), 6000)
      loadUnreadNotifications()
      // Also trigger a native OS notification so it shows even when the tab is focused
      if (Notification.permission === 'granted') {
        navigator.serviceWorker?.ready.then(reg =>
          reg.showNotification(t, {
            body: b,
            icon: '/icon-192.png',
            badge: '/icon-192.png',
            tag: 'coeater-msg', // replaces previous notification of same type
            renotify: true,
          })
        ).catch(() => {})
      }
    })
    return unsub
  }, [])

  const queryString = useMemo(() => {
    const q = new URLSearchParams()
    if (onlyAvailable) q.set('availableOnly', 'true')
    if (dietFilter) q.set('diet', dietFilter)
    if (cityFilter) q.set('city', cityFilter)
    if (gpsCity) q.set('gpsCity', gpsCity)
    return q.toString()
  }, [dietFilter, cityFilter, onlyAvailable, gpsCity])

  async function loadUsers(page = 0) {
    if (page > 0 && loadingUsersRef.current) return
    loadingUsersRef.current = true
    if (page === 0) setLoading(true)
    else setLoadingMore(true)
    try {
      const qs = queryString ? queryString + '&' : ''
      const res = await apiFetch(`/users?${qs}page=${page}&limit=12`)
      if (res.ok) {
        const { users: newUsers, hasMore } = await res.json()
        setUsers(prev => page === 0 ? newUsers : [...prev, ...newUsers])
        setHasMoreUsers(hasMore)
        setUsersPage(page)
      }
    } catch { setInviteStatus('Could not load users.') }
    finally {
      loadingUsersRef.current = false
      if (page === 0) setLoading(false)
      else setLoadingMore(false)
    }
  }

  function loadMoreUsers() {
    if (!hasMoreUsers || loadingUsersRef.current) return
    loadUsers(usersPage + 1)
  }

  async function loadCurrentUser() {
    if (!currentUserId) return
    const res = await apiFetch('/auth/me')
    if (res.ok) setCurrentUser(await res.json())
  }

  async function loadRequests() {
    if (!currentUserId) return
    try {
      const res = await apiFetch(`/requests/${currentUserId}`)
      const data = await res.json()
      setRequests(data)
      data.filter(r => r.status === 'ACCEPTED').forEach(r => {
        socketRef.current?.emit('join_room', r.id)
      })
    } catch {}
  }

  async function loadRatings() {
    if (!currentUserId) return
    try {
      const res = await apiFetch(`/ratings/${currentUserId}`)
      if (res.ok) setRatings(await res.json())
    } catch {}
  }

  useEffect(() => { loadUsers() }, [queryString])

  useEffect(() => {
    if (!currentUserId) return
    loadUnreadNotifications()
    const timer = setInterval(loadUnreadNotifications, 30000)
    return () => clearInterval(timer)
  }, [currentUserId])

  useEffect(() => {
    if (!currentUserId) return
    loadStreakSummary()
    const timer = setInterval(loadStreakSummary, 60000)
    return () => clearInterval(timer)
  }, [currentUserId])

  useEffect(() => {
    if (currentUserId) { loadCurrentUser(); loadRequests(); loadRatings() }
  }, [currentUserId, activeTab])

  function handleAuthenticated(user) {
    setCurrentUserId(user.id)
    setCurrentUser(user)
    setShowAuthModal(false)
    if (!user.profileComplete) setShowCompleteProfile(true)
    connectSocket()
    loadUsers()
    loadUnreadCounts()
    loadUnreadNotifications()
    loadStreakSummary()
    loadGroupChats()
    // Record login with GPS coordinates if available
    const loc = gpsLocationRef.current
    apiFetch('/track/login', {
      method: 'POST',
      body: JSON.stringify({
        latitude:  loc?.latitude  ?? null,
        longitude: loc?.longitude ?? null,
        city:      loc?.city      ?? null,
        address:   loc?.address   ?? null,
        provider:  user.authProvider || null,
        ...buildTrackingContext(),
      })
    }).catch(() => {})
  }

  function handleLogout() {
    clearToken()
    disconnectSocket()
    setCurrentUserId(null)
    setCurrentUser(null)
    setRequests([])
    setMessages({})
    setRatings([])
    setActiveTab('discover')
    setSidebarOpen(false)
    setUnreadChats({})
    setUnreadNotifications(0)
    setStreakCount(0)
    localStorage.removeItem('fcm_token') // force re-registration on next login
  }

  function openComposer(kind = 'post') {
    if (!currentUser) {
      setShowAuthModal(true)
      return
    }
    setActiveTab('home')
    setComposerRequest({ id: Date.now(), kind })
  }

  async function dispatchInvite(receiverId) {
    await registerFcmToken()
    setInviteStatus('')
    const res = await apiFetch('/requests', {
      method: 'POST',
      body: JSON.stringify({ senderId: currentUserId, receiverId, mealTime: new Date(mealTime).toISOString(), note: 'Co-eating invite' })
    })
    setInviteStatus(res.ok ? 'Invite sent! 🎉' : 'Failed to send invite.')
    if (res.ok) { loadRequests(); setTimeout(() => setInviteStatus(''), 3000) }
  }

  function sendInvite(receiverId) {
    // If the user hasn't verified their phone, show the WhatsApp OTP modal first
    if (!currentUser?.phone) {
      setPendingInviteReceiverId(receiverId)
      setShowPhoneOtp(true)
      return
    }
    dispatchInvite(receiverId)
  }

  function handlePhoneVerified(phone) {
    // Update local currentUser with the newly saved phone so modal won't show again
    setCurrentUser(prev => prev ? { ...prev, phone } : prev)
    setShowPhoneOtp(false)
    if (pendingInviteReceiverId != null) {
      dispatchInvite(pendingInviteReceiverId)
      setPendingInviteReceiverId(null)
    }
  }

  async function toggleAvailability() {
    const cur = currentUser?.availability?.isAvailable
    await apiFetch(`/users/${currentUserId}/availability`, {
      method: 'POST', body: JSON.stringify({ isAvailable: !cur, label: currentUser?.availability?.label || '' })
    })
    loadCurrentUser(); loadUsers()
  }

  async function updateRequestStatus(requestId, status) {
    await apiFetch(`/requests/${requestId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) })
    loadRequests()
  }

  function openChat(requestId) {
    setSelectedRequestId(requestId)
    setActiveTab('chats')
    // Mark this chat as read
    apiFetch(`/users/me/chats/${requestId}/read`, { method: 'POST' }).catch(() => {})
    setUnreadChats(u => { const n = { ...u }; delete n[requestId]; return n })
  }

  async function submitRating(requestId, rateeId) {
    if (!ratingScore) return
    await apiFetch('/ratings', {
      method: 'POST',
      body: JSON.stringify({ requestId, rateeId, score: ratingScore, comment: ratingText })
    })
    setRatingScore(0); setRatingText(''); loadRatings()
  }

  const unreadInvites = requests.filter(r => r.receiverId === currentUserId && r.status === 'PENDING').length
  const totalUnreadChats = Object.values(unreadChats).reduce((a, b) => a + b, 0)

  const tabProps = {
    currentUserId, users, loading, requests, selectedRequestId, setSelectedRequestId,
    messages, setMessages, socket: socketRef.current, typingUsers,
    ratings, submitRating, ratingText, setRatingText, ratingScore, setRatingScore,
    unreadCounts: unreadChats,
    onMarkChatRead: (requestId) => {
      apiFetch(`/users/me/chats/${requestId}/read`, { method: 'POST' }).catch(() => {})
      setUnreadChats(u => { const n = { ...u }; delete n[requestId]; return n })
    }
  }

  const isFixed = activeTab === 'chats' || activeTab === 'discover'

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">

      {/* Desktop sidebar */}
      <div className="hidden sm:flex">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} currentUser={currentUser}
          onLogout={handleLogout} onLogin={() => setShowAuthModal(true)} unreadInvites={unreadInvites}
          unreadChats={totalUnreadChats} unreadNotifications={unreadNotifications}
          streakCount={streakCount}
          onFeedback={() => setShowFeedback(true)}
          onCreatePost={() => openComposer('post')} />
      </div>

      {/* Mobile sidebar drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex sm:hidden">
          <div className="flex-1 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <div className="w-64 h-full">
            <Sidebar activeTab={activeTab} setActiveTab={v => { setActiveTab(v); setSidebarOpen(false) }}
              currentUser={currentUser}
              onLogout={() => { handleLogout(); setSidebarOpen(false) }}
              onLogin={() => { setShowAuthModal(true); setSidebarOpen(false) }}
              unreadInvites={unreadInvites}
              unreadChats={totalUnreadChats}
              unreadNotifications={unreadNotifications}
              streakCount={streakCount}
              onFeedback={() => { setShowFeedback(true); setSidebarOpen(false) }}
              onCreatePost={() => { openComposer('post'); setSidebarOpen(false) }} />
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile top bar */}
        {activeTab !== 'reels' && (
          <header className="sm:hidden flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl text-gray-700 hover:bg-orange-50">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            {currentUser && (
              <button onClick={() => openComposer('post')} className="p-2 rounded-xl bg-orange-500 text-white hover:bg-orange-600" aria-label="Create post">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" /></svg>
              </button>
            )}
          </div>
          <p className="title-font font-bold text-gray-900">Co-Eater</p>
          {!currentUser
            ? <button onClick={() => setShowAuthModal(true)} className="rounded-full bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">Login</button>
            : <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveTab('streak')}
                  className="rounded-xl border border-orange-200 bg-orange-50 px-2.5 py-1.5 text-xs font-bold text-orange-700 hover:bg-orange-100"
                >
                  🔥 {streakCount}
                </button>
                <button onClick={() => setActiveTab('notification')} className="relative p-2 rounded-xl text-gray-700 hover:bg-orange-50">
                  🔔
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 rounded-full bg-red-500 text-white text-[10px] min-w-[16px] h-4 px-1 flex items-center justify-center">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </button>
                <button onClick={() => { setProfileUserId(currentUser.id); setActiveTab('profile') }} className="p-0.5 rounded-xl">
                  <img src={currentUser.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(currentUser.name)}`}
                    className="h-8 w-8 rounded-xl object-cover" alt={currentUser.name} />
                </button>
              </div>
          }
          </header>
        )}

        {/* Page content */}
        <main className={
          isFixed
            ? 'flex-1 overflow-hidden flex flex-col px-4 pt-4 pb-20 sm:px-8 sm:pt-6 sm:pb-4'
            : 'flex-1 overflow-y-auto px-4 py-6 sm:px-8 pb-24 sm:pb-8'
        }>
          {activeTab === 'home' && (
            <Home
              currentUser={currentUser}
              onLogin={() => setShowAuthModal(true)}
              city={currentUser?.location?.city || gpsCity || ''}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
              composerRequest={composerRequest}
              onComposerHandled={() => setComposerRequest(null)}
            />
          )}
          {activeTab === 'reels' && (
            <Reels
              currentUser={currentUser}
              currentUserId={currentUserId}
              requests={requests}
              messages={messages}
              socket={socketRef.current}
              onLogin={() => setShowAuthModal(true)}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
              focusReelId={sharedReelId}
              onFocusHandled={() => setSharedReelId(null)}
            />
          )}
          {activeTab === 'discover' && (
            <Discover {...tabProps} currentUser={currentUser} requests={requests} onLogin={() => setShowAuthModal(true)}
              dietFilter={dietFilter} setDietFilter={setDietFilter}
              cityFilter={cityFilter} setCityFilter={setCityFilter} availableCities={availableCities}
              onlyAvailable={onlyAvailable} setOnlyAvailable={setOnlyAvailable}
              mealTime={mealTime} setMealTime={setMealTime}
              loadUsers={loadUsers} loadMoreUsers={loadMoreUsers}
              hasMoreUsers={hasMoreUsers} loadingMore={loadingMore}
              toggleAvailability={toggleAvailability}
              sendInvite={sendInvite} inviteStatus={inviteStatus}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }} />
          )}
          {activeTab === 'group' && (
            <GroupDining
              currentUser={currentUser}
              gpsCity={gpsCity}
              socket={socketRef.current}
              onLogin={() => setShowAuthModal(true)}
              onRequirePhoneVerification={() => setShowPhoneOtp(true)}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
              sharedEventId={sharedGroupEventId}
            />
          )}
          {activeTab === 'restaurants' && (
            <Restaurant
              currentUser={currentUser}
              gpsCity={gpsCity}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
            />
          )}
          {activeTab === 'invites'  && <Invites {...tabProps} onUpdateStatus={updateRequestStatus} />}
          {activeTab === 'chats'    && (
            <Chats
              {...tabProps}
              groupChats={groupChats}
              setGroupChats={setGroupChats}
              onRefreshGroupChats={loadGroupChats}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
              onOpenReel={(reelId) => {
                setSharedReelId(reelId || null)
                setActiveTab('reels')
              }}
            />
          )}
          {activeTab === 'notification' && (
            <NotificationPage
              currentUser={currentUser}
              onLogin={() => setShowAuthModal(true)}
              onAllRead={() => setUnreadNotifications(0)}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
            />
          )}
          {activeTab === 'streak' && (
            <StreakPage
              currentUser={currentUser}
              onLogin={() => setShowAuthModal(true)}
              onStreakCountChange={setStreakCount}
            />
          )}
          {activeTab === 'schedule' && <Schedule currentUser={currentUser} toggleAvailability={toggleAvailability} loadUsers={loadUsers} />}
          {activeTab === 'spots'    && (
            <NearbySpots
              currentUser={currentUser}
              myCoords={myCoords}
              locationError={locationError}
              onLogin={() => setShowAuthModal(true)}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
            />
          )}
          {activeTab === 'spinner'  && <Spinner currentUser={currentUser} />}
          {activeTab === 'settings' && <Settings currentUser={currentUser} loadUsers={loadUsers} />}
          {activeTab === 'refer' && (
            <ReferAndEarn currentUser={currentUser} onLogin={() => setShowAuthModal(true)} />
          )}
          {activeTab === 'profile' && (
            <ProfilePage
              currentUser={currentUser}
              onLogin={() => setShowAuthModal(true)}
              userId={profileUserId || currentUser?.id || null}
              onEditProfile={() => setActiveTab('settings')}
              onOpenProfile={(id) => { setProfileUserId(id || currentUser?.id || null); setActiveTab('profile') }}
            />
          )}
        </main>
      </div>

      <MobileNav activeTab={activeTab} setActiveTab={setActiveTab} unreadInvites={unreadInvites} unreadChats={totalUnreadChats} />

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onAuthenticated={handleAuthenticated} gpsCity={gpsCity} />}

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}

      {showPhoneOtp && (
        <PhoneOtpModal
          onVerified={handlePhoneVerified}
          onClose={() => { setShowPhoneOtp(false); setPendingInviteReceiverId(null) }}
        />
      )}

      {showCompleteProfile && (
        <ProfileCompletionModal
          onClose={() => setShowCompleteProfile(false)}
          onCompleted={user => { setCurrentUser(user); setShowCompleteProfile(false); loadUsers() }}
          gpsCity={gpsCity}
        />
      )}

      {/* Foreground push notification toast */}
      {toast && (
        <div className="fixed bottom-24 right-4 z-50 max-w-xs rounded-2xl bg-white shadow-xl border border-gray-200 p-3 flex gap-3 items-start animate-bounce-once">
          <div className="text-2xl">🔔</div>
          <div>
            <p className="font-semibold text-sm text-gray-900">{toast.title}</p>
            <p className="text-xs text-gray-600 mt-0.5">{toast.body}</p>
          </div>
          <button onClick={() => setToast(null)} className="ml-auto text-gray-400 hover:text-gray-600 text-xs">✕</button>
        </div>
      )}
    </div>
  )
}
